import { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import Box from '../../../../components/Box';
import ContentHeaderStyled from '../../../../components/ContentHeader';
import Flex from '../../../../components/Flex';
import Legend from '../../../../components/Legend';
import { usePageActions } from '../../../../components/PageActions/context';
import PaginationStyled from '../../../../components/Pagination';
import RelatedPlotsStyled from '../../../../components/RelatedPlots';
import Tabs from '../../../../components/Tabs';
import ZoomImage from '../../../../components/ZoomImage';
import { withChartDescriptions } from '../../chartDescriptions';
import ChartSettingsSelect from '../../components/ChartSettingsSelect';
import KitSelectorHint from '../../components/KitSelectorHint';
import LibrariesSelect from '../../components/LibrariesSelect';
import { RelatedPlotsData } from '../../types';

import UsabilityTab from './components/UsabilityTab';
import UseCaseTab from './components/UseCaseTab';
import chartV from './images/chart-v.webp';

const dataChart: RelatedPlotsData[] = withChartDescriptions([
  {
    title: 'Bar Chart - H',
    image: '/images/data-viz-bars/bar-chart-h.png',
    tags: ['comparison'],
    url: '/data-visualization/bars/chart-h',
  },
  {
    title: 'Grouped Bar Chart - H',
    url: '/data-visualization/bars/grouped-bar-chart-h',
    image: '/images/data-viz-bars/grouped-bar-chart-h.png',
    tags: ['comparison'],
  },
  {
    title: 'Stacked Bar Chart - V',
    image: '/images/data-viz-bars/stacked-bar-chart-v.png',
    tags: ['comparison', 'proportion'],
    url: '/data-visualization/bars/stacked-bar-chart-v',
  },
]);

const dataTabs = [
  {
    id: 'tab-use-case',
    title: 'Use Case',
    content: <UseCaseTab />,
  },
  {
    id: 'tab-usability',
    title: 'Usability',
    content: <UsabilityTab />,
  },
];

interface Props {
  className?: string;
}

type SelectData = {
  label: string;
  value: string;
  imagePath: string;
}[];

const selectData: SelectData = [
  {
    label: '',
    value: 'defaultChart',
    imagePath: chartV,
  },
];

type LibraryType = 'builder' | 'powerBI' | 'excel' | 'esri' | 'figma';

const activeChartSettings: Record<LibraryType, SelectData> = {
  powerBI: selectData,
  builder: selectData,
  excel: selectData,
  esri: selectData,
  figma: selectData,
};

const DEFAULT_ACTIVE_LIBRARY = 'builder';

const GroupedBarChartVStructure = ({ className = '' }: Props) => {
  const [selectedTab, setSelectedTab] = useState('tab-use-case');
  const { isFullScreen, onResetSettings } = usePageActions();
  const [activeLibrary, setActiveLibrary] = useState<LibraryType>(DEFAULT_ACTIVE_LIBRARY);
  const [activeChart, setActiveChart] = useState(activeChartSettings[activeLibrary][0].value);
  const theme = useTheme();
  const legendData: Record<string, { label: string; color: string }[]> = {
    defaultChart: [
      { label: 'Higher education', color: theme.datavizQualitative41 },
      { label: 'Primary education', color: theme.datavizQualitative42 },
      { label: 'Secondary education', color: theme.datavizQualitative43 },
    ],
    builder: [],
    excel: [],
  };

  const resetSettingsCallback = () => {
    setActiveLibrary(DEFAULT_ACTIVE_LIBRARY);
    setActiveChart(selectData[0].value);
  };

  const onSetActiveLibrary = (value: LibraryType) => {
    setActiveLibrary(value as LibraryType);
    setActiveChart(activeChartSettings[value as LibraryType][0].value);
  };

  onResetSettings(() => {
    resetSettingsCallback();
  });

  const activeChartData = activeChartSettings[
    activeLibrary as keyof typeof activeChartSettings
  ].find((chart) => chart.value === activeChart);

  return (
    <div className={className}>
      <div className="content">
        <ContentHeaderStyled
          style={{ marginBottom: isFullScreen ? '16px' : undefined }}
          controlBar
          title="Grouped Bar Chart - V"
          breadcrumbs={[
            { label: 'Data Visualization', url: '/data-visualization' },
            { label: 'Bars', url: '/data-visualization/bars' },
            { label: 'Grouped Bar Chart - V' },
          ]}
        />
        <Box
          style={{
            marginBottom: `${isFullScreen ? '0' : '40px'}`,
            border: `1px solid ${theme.neutralBorder}`,
            borderRadius: '6px',
          }}
          as="section"
        >
          {!isFullScreen && (
            <Box
              style={{
                borderBottom: `1px solid ${theme.neutralBorder}`,
                backgroundColor: theme.neutralBackground,
                padding: '12px 16px',
              }}
            >
              <Flex align="center" gap="40px" justify="space-between">
                <KitSelectorHint />
                <LibrariesSelect
                  defaultValue={activeLibrary}
                  onSelect={(value) => onSetActiveLibrary(value as LibraryType)}
                  optionsToExclude={['esri']}
                />
              </Flex>
            </Box>
          )}
          <Box>
            <Box style={{ padding: '46px 24px 34px 24px' }}>
              <Flex justify="space-between">
                {activeChartSettings[activeLibrary][0].label ? (
                  <ChartSettingsSelect
                    defaultValue={activeChart}
                    onSelect={(val) => {
                      setActiveChart(val);
                    }}
                    placeholder="Active Chart Settings"
                    data={activeChartSettings[activeLibrary]}
                  />
                ) : (
                  <div />
                )}
                <Legend type="categorical" data={legendData[activeChart]} />
              </Flex>
            </Box>
            <ZoomImage imgSrc={activeChartData?.imagePath} />
          </Box>
        </Box>

        {!isFullScreen && (
          <>
            <Tabs
              onSelectedTab={setSelectedTab}
              className="tabs-component"
              tabs={dataTabs.map((item) => ({ ...item, content: item.content, title: item.title }))}
            />
            {selectedTab === 'tab-use-case' && (
              <RelatedPlotsStyled
                title="Related Plots"
                description="These charts can be used as alternatives to the Vertical Grouped Bar Chart."
                data={dataChart}
                className="related-plots-section"
              />
            )}
            <PaginationStyled
              previousLink="/data-visualization/bars/chart-h"
              previous="Bar Chart H"
              nextLink="/data-visualization/bars/grouped-bar-chart-h"
              next="Grouped Bar Chart H"
              className="footer-pagination"
            />
          </>
        )}
      </div>
    </div>
  );
};

const GroupedBarChartV = styled(GroupedBarChartVStructure)`
  width: 100%;
  .content {
    margin: 0 auto;
    width: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;

    > .tabs-component {
      margin-bottom: 40px;
    }

    > .footer-pagination {
      display: none;
      margin-top: auto;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        display: block;
      }
    }

    > .related-plots-section {
      margin-bottom: 40px;
    }
  }
`;

export default GroupedBarChartV;
